import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconLock, IconUser, IconLogout, IconBuilding, IconReceipt, IconChartBar, IconDownload, IconUsers, IconPlus, IconEdit, IconTrash, IconKey, IconPercentage, IconCash, IconCheck, IconX, IconFilter, IconRefresh, IconReportMoney } from '@tabler/icons-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { Card } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [salons, setSalons] = useState([]);
  const [payments, setPayments] = useState([]);
  const [masters, setMasters] = useState([]);
  const [services, setServices] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('masters');
  const [syncing, setSyncing] = useState(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('succeeded');

  // Master CRUD state
  const [showMasterModal, setShowMasterModal] = useState(false);
  const [editingMaster, setEditingMaster] = useState(null);
  const [masterForm, setMasterForm] = useState({ name: '', login: '', password: '', salonId: '', masterPercent: '', inn: '', phone: '', cardNumber: '', sbpPhone: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Commission rules state
  const [commissionRules, setCommissionRules] = useState([]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({ master_id: '', salon_id: '', master_percent: 40 });

  // Payouts state
  const [payouts, setPayouts] = useState([]);
  const [payoutFilter, setPayoutFilter] = useState('pending_approval');

  // Reconciliation (сверка) state
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconFrom, setReconFrom] = useState('');
  const [reconTo, setReconTo] = useState('');

  const getAuth = () => {
    const auth = localStorage.getItem('adminAuth');
    return auth ? JSON.parse(auth) : null;
  };

  useEffect(() => {
    const auth = getAuth();
    if (auth) {
      setIsLoggedIn(true);
      loadAdminData(auth);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (activeTab === 'chats') fetchChats();
    if (activeTab === 'masters') loadAllMasters();
    if (activeTab === 'commissions') loadCommissionRules();
    if (activeTab === 'payouts') loadPayouts();
    if (activeTab === 'payments') loadPayments();
    if (activeTab === 'reconciliation') loadReconciliation();
  }, [activeTab, isLoggedIn, payoutFilter, paymentStatusFilter]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.get(`${API}/admin/salons`, { auth: { username, password } });
      const credentials = { username, password };
      localStorage.setItem('adminAuth', JSON.stringify(credentials));
      setIsLoggedIn(true);
      setSalons(response.data);
      toast.success('Вход выполнен');
      loadAdminData(credentials);
    } catch (error) {
      toast.error('Неверные учетные данные');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    setSalons([]);
    setPayments([]);
  };

  const loadAdminData = async (credentials) => {
    try {
      setLoading(true);
      const [salonsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/salons`, { auth: credentials }),
        axios.get(`${API}/admin/payments?status=${paymentStatusFilter}`, { auth: credentials })
      ]);
      setSalons(salonsRes.data);
      setPayments(paymentsRes.data);
      const allMasters = [];
      const allServices = [];
      for (const salon of salonsRes.data) {
        try {
          const [mastersRes, servicesRes] = await Promise.all([
            axios.get(`${API}/salons/${salon.id}/masters`),
            axios.get(`${API}/salons/${salon.id}/services`)
          ]);
          allMasters.push(...mastersRes.data);
          allServices.push(...servicesRes.data);
        } catch (err) { /* skip */ }
      }
      setMasters(allMasters);
      setServices(allServices);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadAllMasters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/masters`);
      setMasters(response.data);
    } catch (error) {
      console.error('Error loading masters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const response = await axios.get(`${API}/admin/payments?status=${paymentStatusFilter}`, { auth });
      setPayments(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadReconciliation = async () => {
    try {
      setReconLoading(true);
      const auth = getAuth();
      const params = [];
      if (reconFrom) params.push(`date_from=${reconFrom}`);
      if (reconTo) params.push(`date_to=${reconTo}`);
      const qs = params.length ? `?${params.join('&')}` : '';
      const res = await axios.get(`${API}/admin/reconciliation${qs}`, { auth });
      setReconData(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Ошибка загрузки сверки');
    } finally {
      setReconLoading(false);
    }
  };

  const loadCommissionRules = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const response = await axios.get(`${API}/admin/commission-rules`, { auth });
      setCommissionRules(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayouts = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const url = payoutFilter === 'all' ? `${API}/admin/payouts?status=all` : `${API}/admin/payouts?status=${payoutFilter}`;
      const response = await axios.get(url, { auth });
      setPayouts(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ============ MASTER CRUD ============

  const openAddMaster = () => {
    setEditingMaster(null);
    setMasterForm({ name: '', login: '', password: '', salonId: salons[0]?.id || '', masterPercent: '', inn: '', phone: '', cardNumber: '', sbpPhone: '' });
    setShowMasterModal(true);
  };

  const openEditMaster = (master) => {
    setEditingMaster(master);
    setMasterForm({
      name: master.name || '',
      login: master.login || '',
      password: '',
      salonId: master.salonId || '',
      masterPercent: master.masterPercent != null ? String(master.masterPercent) : '',
      inn: master.inn || '',
      phone: master.phone || '',
      cardNumber: master.cardNumber || '',
      sbpPhone: master.sbpPhone || '',
    });
    setShowMasterModal(true);
  };

  const saveMaster = async () => {
    const auth = getAuth();
    try {
      if (editingMaster) {
        const updateData = {};
        if (masterForm.name && masterForm.name !== editingMaster.name) updateData.name = masterForm.name;
        if (masterForm.login && masterForm.login !== editingMaster.login) updateData.login = masterForm.login;
        if (masterForm.salonId && masterForm.salonId !== editingMaster.salonId) updateData.salonId = masterForm.salonId;
        if (masterForm.masterPercent !== '') updateData.masterPercent = parseInt(masterForm.masterPercent);
        if (masterForm.inn) updateData.inn = masterForm.inn;
        if (masterForm.phone) updateData.phone = masterForm.phone;
        if (masterForm.cardNumber) updateData.cardNumber = masterForm.cardNumber;
        if (masterForm.sbpPhone) updateData.sbpPhone = masterForm.sbpPhone;
        await axios.put(`${API}/admin/masters/${editingMaster.id}/update`, updateData, { auth });
        toast.success('Мастер обновлён');
      } else {
        if (!masterForm.name || !masterForm.login || !masterForm.password || !masterForm.salonId) {
          toast.error('Заполните обязательные поля');
          return;
        }
        const createData = {
          name: masterForm.name,
          login: masterForm.login,
          password: masterForm.password,
          salonId: masterForm.salonId,
        };
        if (masterForm.masterPercent !== '') createData.masterPercent = parseInt(masterForm.masterPercent);
        if (masterForm.inn) createData.inn = masterForm.inn;
        if (masterForm.phone) createData.phone = masterForm.phone;
        if (masterForm.cardNumber) createData.cardNumber = masterForm.cardNumber;
        if (masterForm.sbpPhone) createData.sbpPhone = masterForm.sbpPhone;
        await axios.post(`${API}/admin/masters/create`, createData, { auth });
        toast.success('Мастер добавлен');
      }
      setShowMasterModal(false);
      loadAllMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const deleteMaster = async (masterId) => {
    const auth = getAuth();
    try {
      await axios.delete(`${API}/admin/masters/${masterId}`, { auth });
      toast.success('Мастер удалён');
      setShowDeleteConfirm(null);
      loadAllMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const resetPassword = async (masterId) => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Пароль минимум 4 символа');
      return;
    }
    const auth = getAuth();
    try {
      await axios.post(`${API}/admin/masters/${masterId}/reset-password`, { new_password: newPassword }, { auth });
      toast.success(`Пароль сброшен: ${newPassword}`);
      setShowPasswordModal(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Ошибка сброса пароля');
    }
  };

  // ============ COMMISSION RULES ============

  const openAddRule = () => {
    setEditingRule(null);
    setRuleForm({ master_id: masters[0]?.id || '', salon_id: salons[0]?.id || '', master_percent: 40 });
    setShowRuleModal(true);
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({ master_id: rule.master_id, salon_id: rule.salon_id, master_percent: rule.master_percent });
    setShowRuleModal(true);
  };

  const saveRule = async () => {
    const auth = getAuth();
    try {
      if (editingRule) {
        await axios.put(`${API}/admin/commission-rules/${editingRule.id}`, ruleForm, { auth });
        toast.success('Правило обновлено');
      } else {
        await axios.post(`${API}/admin/commission-rules`, ruleForm, { auth });
        toast.success('Правило добавлено');
      }
      setShowRuleModal(false);
      loadCommissionRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const deleteRule = async (ruleId) => {
    const auth = getAuth();
    try {
      await axios.delete(`${API}/admin/commission-rules/${ruleId}`, { auth });
      toast.success('Правило удалено');
      loadCommissionRules();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  // ============ PAYOUTS ============

  const approvePayout = async (payoutId) => {
    const auth = getAuth();
    try {
      await axios.post(`${API}/admin/payouts/${payoutId}/approve`, {}, { auth });
      toast.success('Выплата одобрена и отправлена');
      loadPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка одобрения');
    }
  };

  const rejectPayout = async (payoutId) => {
    const auth = getAuth();
    try {
      await axios.post(`${API}/admin/payouts/${payoutId}/reject`, {}, { auth });
      toast.success('Выплата отклонена');
      loadPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка отклонения');
    }
  };

  const retryPayout = async (payoutId) => {
    const auth = getAuth();
    try {
      await axios.post(`${API}/admin/payouts/${payoutId}/retry`, {}, { auth });
      toast.success('Выплата возвращена на одобрение');
      loadPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка повторной попытки');
    }
  };

  // ============ CHATS ============

  const fetchChats = async () => {
    setLoading(true);
    try {
      const auth = getAuth();
      const response = await axios.get(`${API}/admin/chats`, { auth });
      setChats(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить чаты');
    } finally {
      setLoading(false);
    }
  };

  const viewChatHistory = async (chatId) => {
    try {
      const response = await axios.get(`${API}/chat/history/${chatId}`);
      setSelectedChat(chats.find(c => c.id === chatId));
      setChatHistory(response.data.messages);
    } catch (error) {
      toast.error('Не удалось загрузить историю чата');
    }
  };

  const syncPayments = async () => {
    try {
      setSyncing(true);
      const auth = getAuth();
      const response = await axios.post(`${API}/admin/sync-payments`, {}, { auth });
      if (response.data.success) {
        const updated = response.data.results.filter(r => r.new_status && r.new_status !== 'waiting');
        if (updated.length > 0) {
          toast.success(`Обновлено ${updated.length} платежей`);
          loadPayments();
        } else {
          toast.info('Нет платежей для обновления');
        }
      }
    } catch (error) {
      toast.error('Ошибка синхронизации');
    } finally {
      setSyncing(false);
    }
  };

  // ============ HELPERS ============

  const fmt = (d) => d ? new Date(d).toLocaleString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Samara' }) : '';
  const fmtAmount = (a) => (!a && a !== 0) ? '0' : (a / 100).toFixed(0);
  const getSalonName = (salonId) => { const s = salons.find(x => x.id === salonId); return s ? s.name : 'Неизвестно'; };
  const getMasterName = (masterId) => { const m = masters.find(x => x.id === masterId); return m ? m.name : 'Неизвестно'; };

  const normalizePayment = (p) => ({
    id: p.id, salonId: p.salonId || p.salon_id, masterId: p.masterId || p.master_id,
    serviceName: p.service_name, totalAmount: p.totalAmount || p.total_amount || 0,
    masterAmount: p.masterAmount || p.master_amount || 0, companyAmount: p.companyAmount || p.salon_amount || 0,
    status: p.status, createdAt: p.createdAt || p.created_at, orderId: p.order_id || p.yooPaymentId
  });

  const exportToExcel = () => {
    if (payments.length === 0) { toast.error('Нет данных'); return; }
    const excelData = payments.map((raw, i) => {
      const p = normalizePayment(raw);
      return { '№': i + 1, 'Салон': getSalonName(p.salonId), 'Дата': fmt(p.createdAt), 'Услуга': p.serviceName || '-', 'Сумма': fmtAmount(p.totalAmount) + ' ₽', 'Мастер': getMasterName(p.masterId), 'Статус': p.status === 'succeeded' ? 'Успешно' : p.status };
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws, 'Транзакции');
    XLSX.writeFile(wb, `rio_transactions_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Файл экспортирован');
  };

  const methodLabel = (m) => ({
    FASTER_PAYMENTS: 'СБП', SBERPAY: 'SberPay', ALFAPAY: 'AlfaPay', MARKETPLACE: 'СБП',
    CCVISAMC: 'Карта', UNKNOWN: '—',
  }[m] || m);

  const rub = (k) => (!k && k !== 0 ? '0.00' : (k / 100).toFixed(2));

  const exportReconciliation = () => {
    if (!reconData || reconData.rows.length === 0) { toast.error('Нет данных'); return; }
    const data = reconData.rows.map((r, i) => ({
      '№': i + 1,
      'Заказ': r.order_id,
      'Дата': fmt(r.created_at),
      'Салон': r.salon_name,
      'Мастер': r.master_name,
      'Услуга': r.service_name || '-',
      'Сумма, ₽': rub(r.total_amount),
      'Метод': methodLabel(r.payment_method),
      'Реальная ставка YPMN, %': r.real_rate,
      'Реальная комиссия YPMN, ₽': rub(r.real_fee),
      'Заложено в сплит (3,7%), ₽': rub(r.our_commission),
      'Разница (переплата), ₽': rub(r.difference),
      'Мастеру, ₽': rub(r.master_amount),
      'Салону, ₽': rub(r.salon_amount),
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Сверка');
    XLSX.writeFile(wb, `rio_reconciliation_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Сверка экспортирована');
  };

  const payoutStatusBadge = (status) => {
    const map = {
      pending_approval: { label: 'Ожидает одобрения', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
      processing: { label: 'В обработке', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
      succeeded: { label: 'Выплачено', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
      failed: { label: 'Ошибка', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
      rejected: { label: 'Отклонено', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
      pending: { label: 'Ожидание', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
    };
    const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
    return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>{m.label}</span>;
  };

  // ============ LOGIN ============

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background bg-noise flex items-center justify-center p-4">
        <Card className="max-w-sm w-full bg-card/70 backdrop-blur-sm border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[24px] p-6">
          <div className="mb-6 text-center">
            <h2 className="font-['Playfair_Display'] text-2xl mb-2">Вход в админ-панель</h2>
            <p className="text-sm text-[color:hsl(var(--foreground))]/70">Введите логин и пароль</p>
          </div>
          <form onSubmit={handleLogin} data-testid="login-form">
            <div className="mb-4">
              <label className="block text-sm mb-1" htmlFor="username">Логин</label>
              <div className="relative">
                <IconUser size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:hsl(var(--foreground))]/50" />
                <input id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full h-10 pl-9 pr-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" required data-testid="login-form-username-input" />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm mb-1" htmlFor="password">Пароль</label>
              <div className="relative">
                <IconLock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:hsl(var(--foreground))]/50" />
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-10 pl-9 pr-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" required data-testid="login-form-password-input" />
              </div>
            </div>
            <button type="submit" className="w-full inline-flex items-center justify-center h-11 px-6 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white transition-[background-color] duration-200 ease-out hover:bg-[color:hsl(var(--brand-orange-700))]" data-testid="login-form-submit-button">Войти</button>
          </form>
          <button onClick={() => navigate('/')} className="w-full mt-4 text-sm text-[color:hsl(var(--foreground))]/70 hover:text-[color:hsl(var(--foreground))] transition-colors">&larr; Вернуться на главную</button>
        </Card>
      </div>
    );
  }

  // ============ TABS CONFIG ============

  const tabs = [
    { id: 'masters', label: 'Мастера', icon: IconUsers },
    { id: 'commissions', label: 'Проценты', icon: IconPercentage },
    { id: 'payouts', label: 'Выплаты', icon: IconCash },
    { id: 'payments', label: 'Транзакции', icon: IconReceipt },
    { id: 'reconciliation', label: 'Сверка', icon: IconReportMoney },
    { id: 'salons', label: 'Салоны', icon: IconBuilding },
    { id: 'analytics', label: 'Аналитика', icon: IconChartBar },
    { id: 'chats', label: 'Чаты', icon: IconChartBar },
  ];

  const pendingPayoutsCount = payouts.filter(p => p.status === 'pending_approval').length;

  return (
    <div className="min-h-screen bg-background bg-noise">
      {/* Header */}
      <header className="border-b-[0.5px] border-[color:hsl(var(--hairline))] bg-card/60 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <h1 className="font-['Playfair_Display'] text-xl font-semibold">Админ-панель</h1>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={handleLogout} className="inline-flex items-center gap-2 px-4 h-9 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] hover:border-[color:hsl(var(--brand-orange))] transition-[border-color] duration-200" data-testid="logout-button">
              <IconLogout size={16} /><span className="text-sm">Выйти</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b-[0.5px] border-[color:hsl(var(--hairline))] overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap flex items-center gap-2 ${activeTab === tab.id ? 'text-[color:hsl(var(--brand-orange))]' : 'text-[color:hsl(var(--foreground))]/70 hover:text-[color:hsl(var(--foreground))]'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon size={16} />
              {tab.label}
              {tab.id === 'payouts' && pendingPayoutsCount > 0 && (
                <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{pendingPayoutsCount}</span>
              )}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[color:hsl(var(--brand-orange))]" />}
            </button>
          ))}
        </div>

        {/* ============ МАСТЕРА TAB ============ */}
        {activeTab === 'masters' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-['Playfair_Display'] text-2xl">Управление мастерами</h2>
              <button onClick={openAddMaster} className="flex items-center gap-2 px-4 h-10 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors text-sm font-medium" data-testid="add-master-btn">
                <IconPlus size={16} /> Добавить мастера
              </button>
            </div>
            {loading ? <Skeleton className="h-96" /> : masters.length === 0 ? (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-12 text-center">
                <p className="text-[color:hsl(var(--foreground))]/70">Мастеров пока нет</p>
              </Card>
            ) : (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0" data-testid="masters-table">
                    <thead>
                      <tr>
                        {['Имя', 'Логин', 'Филиал', 'Процент', 'ИНН', 'Карта', 'Действия'].map(h => (
                          <th key={h} className="text-left text-[12px] font-medium text-[color:hsl(var(--foreground))]/70 px-3 py-3 border-b-[0.5px] border-[color:hsl(var(--hairline))] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {masters.map(master => (
                        <tr key={master.id} className="hover:bg-[color:hsl(var(--muted))]/40 transition-colors" data-testid="master-row">
                          <td className="px-3 py-3 text-[13px] font-medium whitespace-nowrap">{master.name}</td>
                          <td className="px-3 py-3 text-[12px] font-mono text-[color:hsl(var(--foreground))]/70 whitespace-nowrap">{master.login}</td>
                          <td className="px-3 py-3 text-[12px] whitespace-nowrap">{getSalonName(master.salonId)}</td>
                          <td className="px-3 py-3 text-[13px] whitespace-nowrap">
                            <span className="text-[color:hsl(var(--brand-orange))] font-bold">{master.masterPercent != null ? `${master.masterPercent}%` : 'По ум.'}</span>
                          </td>
                          <td className="px-3 py-3 text-[11px] font-mono text-[color:hsl(var(--foreground))]/50 whitespace-nowrap">{master.inn || '-'}</td>
                          <td className="px-3 py-3 text-[11px] font-mono text-[color:hsl(var(--foreground))]/50 whitespace-nowrap">{master.cardNumber || '-'}</td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <button onClick={() => openEditMaster(master)} className="p-1.5 rounded-lg hover:bg-[color:hsl(var(--muted))] transition-colors" title="Редактировать" data-testid={`edit-master-${master.id}`}>
                                <IconEdit size={14} className="text-[color:hsl(var(--brand-orange))]" />
                              </button>
                              <button onClick={() => { setShowPasswordModal(master.id); setNewPassword(''); }} className="p-1.5 rounded-lg hover:bg-[color:hsl(var(--muted))] transition-colors" title="Сбросить пароль" data-testid={`reset-pwd-${master.id}`}>
                                <IconKey size={14} className="text-blue-500" />
                              </button>
                              <button onClick={() => setShowDeleteConfirm(master.id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Удалить" data-testid={`delete-master-${master.id}`}>
                                <IconTrash size={14} className="text-red-500" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ============ ПРОЦЕНТЫ TAB ============ */}
        {activeTab === 'commissions' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-['Playfair_Display'] text-2xl">Процент мастер / салон</h2>
                <p className="text-sm text-[color:hsl(var(--foreground))]/70 mt-1">Комиссионные правила определяют распределение оплаты между мастером и салоном. Выберите мастера и салон из списка.</p>
              </div>
              <button onClick={openAddRule} className="flex items-center gap-2 px-4 h-10 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors text-sm font-medium" data-testid="add-rule-btn">
                <IconPlus size={16} /> Добавить правило
              </button>
            </div>
            {loading ? <Skeleton className="h-64" /> : commissionRules.length === 0 ? (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-12 text-center space-y-4">
                <p className="text-[color:hsl(var(--foreground))]/70">Правил пока нет. Используются проценты из настроек мастеров или салонов по умолчанию (40% мастер / 60% салон).</p>
                <button 
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await axios.post(`${API}/admin/commission-rules/init-defaults`, {}, { auth });
                      loadCommissionRules();
                    } catch (err) {
                      alert('Ошибка инициализации: ' + (err.response?.data?.detail || err.message));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="px-6 h-10 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors text-sm font-medium"
                  data-testid="init-rules-btn"
                >
                  Создать правила для всех мастеров (40% / 60%)
                </button>
              </Card>
            ) : (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0" data-testid="commission-rules-table">
                    <thead>
                      <tr>
                        {['Мастер', 'Салон', 'Мастеру', 'Салону', 'Действия'].map(h => (
                          <th key={h} className="text-left text-[12px] font-medium text-[color:hsl(var(--foreground))]/70 px-4 py-3 border-b-[0.5px] border-[color:hsl(var(--hairline))] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {commissionRules.map(rule => (
                        <tr key={rule.id} className="hover:bg-[color:hsl(var(--muted))]/40 transition-colors" data-testid="commission-rule-row">
                          <td className="px-4 py-3 text-[13px] font-medium whitespace-nowrap">{getMasterName(rule.master_id)}</td>
                          <td className="px-4 py-3 text-[13px] whitespace-nowrap">{getSalonName(rule.salon_id)}</td>
                          <td className="px-4 py-3 text-[13px] whitespace-nowrap"><span className="text-[color:hsl(var(--brand-orange))] font-bold">{rule.master_percent}%</span></td>
                          <td className="px-4 py-3 text-[13px] whitespace-nowrap"><span className="text-blue-600 dark:text-blue-400 font-bold">{100 - rule.master_percent}%</span></td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <button onClick={() => openEditRule(rule)} className="p-1.5 rounded-lg hover:bg-[color:hsl(var(--muted))] transition-colors" data-testid={`edit-rule-${rule.id}`}>
                                <IconEdit size={14} className="text-[color:hsl(var(--brand-orange))]" />
                              </button>
                              <button onClick={() => deleteRule(rule.id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-testid={`delete-rule-${rule.id}`}>
                                <IconTrash size={14} className="text-red-500" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ============ ВЫПЛАТЫ TAB ============ */}
        {activeTab === 'payouts' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-['Playfair_Display'] text-2xl">Управление выплатами</h2>
              <div className="flex gap-2">
                {['pending_approval', 'all', 'succeeded', 'failed', 'rejected'].map(f => (
                  <button key={f} onClick={() => { setPayoutFilter(f); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${payoutFilter === f ? 'bg-[color:hsl(var(--brand-orange))] text-white' : 'bg-[color:hsl(var(--muted))] text-[color:hsl(var(--foreground))]/70 hover:text-[color:hsl(var(--foreground))]'}`}
                    data-testid={`payout-filter-${f}`}>
                    {f === 'pending_approval' ? 'Ожидают' : f === 'all' ? 'Все' : f === 'succeeded' ? 'Выплачено' : f === 'failed' ? 'Ошибки' : 'Отклонено'}
                  </button>
                ))}
              </div>
            </div>
            {loading ? <Skeleton className="h-64" /> : payouts.length === 0 ? (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-12 text-center">
                <p className="text-[color:hsl(var(--foreground))]/70">Нет выплат</p>
              </Card>
            ) : (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0" data-testid="payouts-table">
                    <thead>
                      <tr>
                        {['Мастер', 'Сумма', 'Карта', 'Дата', 'Статус', 'Действия'].map(h => (
                          <th key={h} className="text-left text-[12px] font-medium text-[color:hsl(var(--foreground))]/70 px-4 py-3 border-b-[0.5px] border-[color:hsl(var(--hairline))] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map(p => (
                        <tr key={p.id} className="hover:bg-[color:hsl(var(--muted))]/40 transition-colors" data-testid="payout-row">
                          <td className="px-4 py-3 text-[13px] font-medium whitespace-nowrap">{p.master_name || getMasterName(p.master_id)}</td>
                          <td className="px-4 py-3 text-[13px] font-bold tabular-nums whitespace-nowrap">{fmtAmount(p.amount)} ₽</td>
                          <td className="px-4 py-3 text-[11px] font-mono text-[color:hsl(var(--foreground))]/50 whitespace-nowrap">{p.recipient_card || '-'}</td>
                          <td className="px-4 py-3 text-[12px] tabular-nums text-[color:hsl(var(--foreground))]/70 whitespace-nowrap">{fmt(p.created_at)}</td>
                          <td className="px-4 py-3">{payoutStatusBadge(p.status)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {p.status === 'pending_approval' && (
                              <div className="flex items-center gap-2">
                                <button onClick={() => approvePayout(p.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 transition-colors" data-testid={`approve-payout-${p.id}`}>
                                  <IconCheck size={14} /> Одобрить
                                </button>
                                <button onClick={() => rejectPayout(p.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors" data-testid={`reject-payout-${p.id}`}>
                                  <IconX size={14} /> Отклонить
                                </button>
                              </div>
                            )}
                            {(p.status === 'failed' || p.status === 'approved_no_ypmn') && (
                              <button onClick={() => retryPayout(p.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 transition-colors" data-testid={`retry-payout-${p.id}`}>
                                <IconRefresh size={14} /> Повторить
                              </button>
                            )}
                            {!['pending_approval', 'failed', 'approved_no_ypmn'].includes(p.status) && (
                              <span className="text-xs text-[color:hsl(var(--foreground))]/40">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ============ ТРАНЗАКЦИИ TAB ============ */}
        {activeTab === 'payments' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Playfair_Display'] text-2xl">Транзакции</h2>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {[{ v: 'succeeded', l: 'Успешные' }, { v: 'all', l: 'Все' }, { v: 'expired', l: 'Истёкшие' }].map(f => (
                    <button key={f.v} onClick={() => { setPaymentStatusFilter(f.v); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${paymentStatusFilter === f.v ? 'bg-[color:hsl(var(--brand-orange))] text-white' : 'bg-[color:hsl(var(--muted))] text-[color:hsl(var(--foreground))]/70'}`}
                      data-testid={`payment-filter-${f.v}`}>{f.l}</button>
                  ))}
                </div>
                <button onClick={syncPayments} disabled={syncing} className="inline-flex items-center gap-2 px-4 h-10 rounded-[12px] bg-[color:hsl(var(--muted))] text-[color:hsl(var(--foreground))] transition-colors hover:bg-[color:hsl(var(--muted))]/80 disabled:opacity-50" data-testid="sync-payments-button">
                  <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  <span className="text-sm">{syncing ? 'Синхронизация...' : 'Обновить'}</span>
                </button>
                <button onClick={exportToExcel} className="inline-flex items-center gap-2 px-4 h-10 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors" data-testid="export-excel-button">
                  <IconDownload size={16} /><span className="text-sm">Excel</span>
                </button>
              </div>
            </div>
            {loading ? <Skeleton className="h-96" /> : payments.length === 0 ? (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-12 text-center"><p className="text-[color:hsl(var(--foreground))]/70">Транзакций нет</p></Card>
            ) : (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0" data-testid="transactions-table">
                    <thead><tr>
                      {['Салон', 'Дата', 'Услуга', 'Сумма', 'Мастер', 'Статус'].map(h => (
                        <th key={h} className="text-left text-[12px] font-medium text-[color:hsl(var(--foreground))]/70 px-3 py-3 border-b-[0.5px] border-[color:hsl(var(--hairline))] whitespace-nowrap">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {payments.map(raw => {
                        const p = normalizePayment(raw);
                        return (
                          <tr key={p.id} className="hover:bg-[color:hsl(var(--muted))]/40 transition-colors" data-testid="transactions-row">
                            <td className="px-3 py-3 text-[13px] whitespace-nowrap">{getSalonName(p.salonId)}</td>
                            <td className="px-3 py-3 text-[12px] tabular-nums text-[color:hsl(var(--foreground))]/70 whitespace-nowrap">{fmt(p.createdAt)}</td>
                            <td className="px-3 py-3 text-[13px] whitespace-nowrap">{p.serviceName || '-'}</td>
                            <td className="px-3 py-3 text-[13px] tabular-nums whitespace-nowrap">{fmtAmount(p.totalAmount)} ₽</td>
                            <td className="px-3 py-3 text-[13px] whitespace-nowrap">{getMasterName(p.masterId)}</td>
                            <td className="px-3 py-3"><span className={`inline-block px-2 py-1 rounded-full text-xs ${p.status === 'succeeded' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : p.status === 'expired' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`}>
                              {p.status === 'succeeded' ? 'Успешно' : p.status === 'expired' ? 'Истёк' : p.status === 'waiting' ? 'Ожидание' : p.status}
                            </span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ============ СВЕРКА TAB ============ */}
        {activeTab === 'reconciliation' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="font-['Playfair_Display'] text-2xl">Сверка комиссии</h2>
                <p className="text-sm text-[color:hsl(var(--foreground))]/70 mt-1">Сравнение реальной комиссии YPMN (1,6% СБП / 3,7% карта) с заложенной в сплит (3,7%)</p>
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <div>
                  <label className="block text-xs mb-1 text-[color:hsl(var(--foreground))]/70">С даты</label>
                  <input type="date" value={reconFrom} onChange={(e) => setReconFrom(e.target.value)} className="h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card text-sm" data-testid="recon-date-from" />
                </div>
                <div>
                  <label className="block text-xs mb-1 text-[color:hsl(var(--foreground))]/70">По дату</label>
                  <input type="date" value={reconTo} onChange={(e) => setReconTo(e.target.value)} className="h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card text-sm" data-testid="recon-date-to" />
                </div>
                <button onClick={loadReconciliation} className="inline-flex items-center gap-2 px-4 h-10 rounded-[12px] bg-[color:hsl(var(--muted))] text-[color:hsl(var(--foreground))] hover:bg-[color:hsl(var(--muted))]/80 transition-colors" data-testid="recon-apply-button">
                  <IconFilter size={16} /><span className="text-sm">Сформировать</span>
                </button>
                <button onClick={exportReconciliation} className="inline-flex items-center gap-2 px-4 h-10 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors" data-testid="recon-export-button">
                  <IconDownload size={16} /><span className="text-sm">Excel</span>
                </button>
              </div>
            </div>

            {reconLoading ? <Skeleton className="h-96" /> : !reconData ? (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-12 text-center"><p className="text-[color:hsl(var(--foreground))]/70">Нажмите «Сформировать»</p></Card>
            ) : (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="recon-summary">
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[16px] p-4">
                    <p className="text-xs text-[color:hsl(var(--foreground))]/60 mb-1">Оборот ({reconData.summary.count} опл.)</p>
                    <p className="text-xl font-semibold tabular-nums">{rub(reconData.summary.total)} ₽</p>
                  </Card>
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[16px] p-4">
                    <p className="text-xs text-[color:hsl(var(--foreground))]/60 mb-1">Реальная комиссия YPMN</p>
                    <p className="text-xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{rub(reconData.summary.real_fee)} ₽</p>
                  </Card>
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[16px] p-4">
                    <p className="text-xs text-[color:hsl(var(--foreground))]/60 mb-1">Заложено в сплит (3,7%)</p>
                    <p className="text-xl font-semibold tabular-nums">{rub(reconData.summary.our_commission)} ₽</p>
                  </Card>
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[16px] p-4">
                    <p className="text-xs text-[color:hsl(var(--foreground))]/60 mb-1">Разница (переплата)</p>
                    <p className="text-xl font-semibold tabular-nums text-red-600 dark:text-red-400" data-testid="recon-overpay">{rub(reconData.summary.difference)} ₽</p>
                  </Card>
                </div>

                {/* By method breakdown */}
                <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[16px] p-4">
                  <p className="text-sm font-medium mb-3">Разбивка по методам оплаты</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs text-[color:hsl(var(--foreground))]/60">
                        <th className="py-2 pr-4">Метод</th><th className="py-2 pr-4">Опл.</th><th className="py-2 pr-4">Оборот ₽</th><th className="py-2 pr-4">Реальная YPMN ₽</th><th className="py-2 pr-4">Заложено 3,7% ₽</th><th className="py-2">Переплата ₽</th>
                      </tr></thead>
                      <tbody>
                        {Object.entries(reconData.by_method).map(([m, v]) => (
                          <tr key={m} className="border-t-[0.5px] border-[color:hsl(var(--hairline))]">
                            <td className="py-2 pr-4">{methodLabel(m)}</td>
                            <td className="py-2 pr-4 tabular-nums">{v.count}</td>
                            <td className="py-2 pr-4 tabular-nums">{rub(v.total)}</td>
                            <td className="py-2 pr-4 tabular-nums text-emerald-600 dark:text-emerald-400">{rub(v.real_fee)}</td>
                            <td className="py-2 pr-4 tabular-nums">{rub(v.our_commission)}</td>
                            <td className="py-2 tabular-nums text-red-600 dark:text-red-400">{rub(v.difference)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Detailed table */}
                <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full border-separate border-spacing-0" data-testid="recon-table">
                      <thead><tr>
                        {['Дата', 'Салон', 'Мастер', 'Сумма', 'Метод', 'Ставка', 'Реальная YPMN', 'Заложено 3,7%', 'Переплата'].map(h => (
                          <th key={h} className="text-left text-[12px] font-medium text-[color:hsl(var(--foreground))]/70 px-3 py-3 border-b-[0.5px] border-[color:hsl(var(--hairline))] whitespace-nowrap">{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {reconData.rows.map((r, i) => (
                          <tr key={r.order_id || i} className="hover:bg-[color:hsl(var(--muted))]/40 transition-colors" data-testid="recon-row">
                            <td className="px-3 py-3 text-[12px] tabular-nums text-[color:hsl(var(--foreground))]/70 whitespace-nowrap">{fmt(r.created_at)}</td>
                            <td className="px-3 py-3 text-[13px] whitespace-nowrap">{r.salon_name || '—'}</td>
                            <td className="px-3 py-3 text-[13px] whitespace-nowrap">{r.master_name || '—'}</td>
                            <td className="px-3 py-3 text-[13px] tabular-nums whitespace-nowrap">{rub(r.total_amount)} ₽</td>
                            <td className="px-3 py-3 text-[13px] whitespace-nowrap">{methodLabel(r.payment_method)}</td>
                            <td className="px-3 py-3 text-[13px] tabular-nums whitespace-nowrap">{r.real_rate}%</td>
                            <td className="px-3 py-3 text-[13px] tabular-nums whitespace-nowrap text-emerald-600 dark:text-emerald-400">{rub(r.real_fee)} ₽</td>
                            <td className="px-3 py-3 text-[13px] tabular-nums whitespace-nowrap">{rub(r.our_commission)} ₽</td>
                            <td className="px-3 py-3 text-[13px] tabular-nums whitespace-nowrap text-red-600 dark:text-red-400">{rub(r.difference)} ₽</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {/* ============ САЛОНЫ TAB ============ */}
        {activeTab === 'salons' && (
          <div className="space-y-6">
            <h2 className="font-['Playfair_Display'] text-2xl">Список салонов</h2>
            {loading ? <div className="space-y-4">{[1, 2].map(i => <Skeleton key={i} className="h-24" />)}</div> : (
              <div className="space-y-4">
                {salons.map(salon => (
                  <Card key={salon.id} className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6" data-testid="salon-card-admin">
                    <h3 className="font-['Playfair_Display'] text-lg mb-1">{salon.name}</h3>
                    <p className="text-sm text-[color:hsl(var(--foreground))]/70">{salon.city}</p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============ АНАЛИТИКА TAB ============ */}
        {activeTab === 'analytics' && (
          <div className="space-y-8">
            <h2 className="font-['Playfair_Display'] text-2xl">Аналитика</h2>
            {payments.length === 0 ? (
              <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-12 text-center"><p className="text-[color:hsl(var(--foreground))]/70">Недостаточно данных</p></Card>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6">
                    <p className="text-sm text-[color:hsl(var(--foreground))]/60 mb-2">Общая выручка</p>
                    <p className="text-3xl font-bold text-[color:hsl(var(--brand-orange))]">{fmtAmount(payments.map(normalizePayment).reduce((s, p) => s + (p.status === 'succeeded' ? p.totalAmount : 0), 0))} ₽</p>
                  </Card>
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6">
                    <p className="text-sm text-[color:hsl(var(--foreground))]/60 mb-2">Мастерам</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{fmtAmount(payments.map(normalizePayment).reduce((s, p) => s + (p.status === 'succeeded' ? p.masterAmount : 0), 0))} ₽</p>
                  </Card>
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6">
                    <p className="text-sm text-[color:hsl(var(--foreground))]/60 mb-2">Компании</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{fmtAmount(payments.map(normalizePayment).reduce((s, p) => s + (p.status === 'succeeded' ? p.companyAmount : 0), 0))} ₽</p>
                  </Card>
                </div>
                <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6">
                  <h3 className="font-medium text-lg mb-4">Распределение выручки</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={[
                        { name: 'Мастерам', value: payments.map(normalizePayment).reduce((s, p) => s + (p.status === 'succeeded' ? p.masterAmount : 0), 0) / 100, fill: '#10b981' },
                        { name: 'Компании', value: payments.map(normalizePayment).reduce((s, p) => s + (p.status === 'succeeded' ? p.companyAmount : 0), 0) / 100, fill: '#3b82f6' }
                      ]} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={100} dataKey="value" />
                      <Tooltip formatter={(v) => `${v.toFixed(0)} ₽`} /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </>
            )}
          </div>
        )}

        {/* ============ ЧАТЫ TAB ============ */}
        {activeTab === 'chats' && (
          <div className="space-y-6" data-testid="chats-content">
            <Card className="p-6 rounded-[24px] overflow-hidden">
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-6">История чатов</h2>
              {loading ? <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-[24px]" />)}</div>
              : chats.length === 0 ? <div className="text-center py-12"><p className="text-foreground/60">Нет чатов</p></div>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full whitespace-nowrap">
                    <thead><tr className="border-b-[0.5px] border-border">
                      {['Email', 'Сообщений', 'Последнее', 'Создан', 'Действия'].map(h => <th key={h} className="text-left py-3 px-4 font-medium">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {chats.map(chat => (
                        <tr key={chat.id} className="border-b-[0.5px] border-border hover:bg-muted/50 transition-colors">
                          <td className="py-4 px-4">{chat.email || <span className="text-foreground/40">Не указан</span>}</td>
                          <td className="py-4 px-4"><span className="px-3 py-1 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium">{chat.messagesCount}</span></td>
                          <td className="py-4 px-4 max-w-md"><p className="text-sm text-foreground/60 truncate">{chat.lastMessage}</p></td>
                          <td className="py-4 px-4 text-sm text-foreground/60">{fmt(chat.createdAt)}</td>
                          <td className="py-4 px-4">
                            <button onClick={() => viewChatHistory(chat.id)} className="px-4 py-2 bg-[#FF6B35] text-white rounded-[24px] text-sm font-medium hover:bg-[#E05A2E] transition-colors" data-testid={`view-chat-${chat.id}`}>Просмотр</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            {selectedChat && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedChat(null); setChatHistory([]); }}>
                <div className="bg-card rounded-[24px] max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="chat-history-modal">
                  <div className="p-6 border-b-[0.5px] border-border flex justify-between items-center">
                    <div><h3 className="text-xl font-['Playfair_Display'] font-semibold">История чата</h3>{selectedChat.email && <p className="text-sm text-foreground/60 mt-1">{selectedChat.email}</p>}</div>
                    <button onClick={() => { setSelectedChat(null); setChatHistory([]); }} className="p-2 hover:bg-muted rounded-lg"><span className="text-2xl">&times;</span></button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {chatHistory.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] px-4 py-3 rounded-[18px] ${msg.role === 'user' ? 'bg-[#FF6B35] text-white rounded-br-[4px]' : 'bg-muted rounded-bl-[4px]'}`}>
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============ MODALS ============ */}

      {/* Master Add/Edit Modal */}
      {showMasterModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="master-modal">
          <Card className="max-w-lg w-full bg-card rounded-[24px] p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-['Playfair_Display'] text-xl">{editingMaster ? 'Редактировать мастера' : 'Добавить мастера'}</h3>
              <button onClick={() => setShowMasterModal(false)} className="p-1 rounded-lg hover:bg-[color:hsl(var(--muted))]"><IconX size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Имя *</label>
                <input type="text" value={masterForm.name} onChange={e => setMasterForm(f => ({ ...f, name: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" placeholder="Иванова Мария" data-testid="master-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Логин *</label>
                  <input type="text" value={masterForm.login} onChange={e => setMasterForm(f => ({ ...f, login: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" placeholder="maria" data-testid="master-form-login" />
                </div>
                {!editingMaster && (
                  <div>
                    <label className="block text-sm mb-1">Пароль *</label>
                    <input type="text" value={masterForm.password} onChange={e => setMasterForm(f => ({ ...f, password: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" placeholder="maria123" data-testid="master-form-password" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Филиал *</label>
                  <select value={masterForm.salonId} onChange={e => setMasterForm(f => ({ ...f, salonId: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" data-testid="master-form-salon">
                    <option value="">Выберите салон</option>
                    {salons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Процент мастера</label>
                  <input type="number" min="0" max="100" value={masterForm.masterPercent} onChange={e => setMasterForm(f => ({ ...f, masterPercent: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" placeholder="60" data-testid="master-form-percent" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">ИНН</label>
                  <input type="text" value={masterForm.inn} onChange={e => setMasterForm(f => ({ ...f, inn: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" placeholder="000000000000" data-testid="master-form-inn" />
                </div>
                <div>
                  <label className="block text-sm mb-1">Телефон</label>
                  <input type="text" value={masterForm.phone} onChange={e => setMasterForm(f => ({ ...f, phone: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" placeholder="+7 (000) 000-00-00" data-testid="master-form-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Номер карты</label>
                  <input type="text" value={masterForm.cardNumber} onChange={e => setMasterForm(f => ({ ...f, cardNumber: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" placeholder="2200****0000" data-testid="master-form-card" />
                </div>
                <div>
                  <label className="block text-sm mb-1">Телефон СБП</label>
                  <input type="text" value={masterForm.sbpPhone} onChange={e => setMasterForm(f => ({ ...f, sbpPhone: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" placeholder="+7 (000) 000-00-00" data-testid="master-form-sbp" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowMasterModal(false)} className="flex-1 h-11 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] text-sm font-medium">Отмена</button>
              <button onClick={saveMaster} className="flex-1 h-11 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white text-sm font-medium hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors" data-testid="master-form-save">{editingMaster ? 'Сохранить' : 'Добавить'}</button>
            </div>
          </Card>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="password-modal">
          <Card className="max-w-sm w-full bg-card rounded-[24px] p-6 shadow-2xl">
            <h3 className="font-['Playfair_Display'] text-xl mb-4">Сброс пароля</h3>
            <p className="text-sm text-[color:hsl(var(--foreground))]/70 mb-4">Мастер: <strong>{masters.find(m => m.id === showPasswordModal)?.name}</strong></p>
            <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))] mb-4" placeholder="Новый пароль" data-testid="new-password-input" />
            <div className="flex gap-3">
              <button onClick={() => setShowPasswordModal(null)} className="flex-1 h-10 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] text-sm">Отмена</button>
              <button onClick={() => resetPassword(showPasswordModal)} className="flex-1 h-10 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white text-sm font-medium hover:bg-[color:hsl(var(--brand-orange-700))]" data-testid="confirm-reset-pwd">Сбросить</button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="delete-confirm-modal">
          <Card className="max-w-sm w-full bg-card rounded-[24px] p-6 shadow-2xl">
            <h3 className="font-['Playfair_Display'] text-xl mb-4">Удалить мастера?</h3>
            <p className="text-sm text-[color:hsl(var(--foreground))]/70 mb-6">Мастер <strong>{masters.find(m => m.id === showDeleteConfirm)?.name}</strong> будет удалён. Это действие нельзя отменить.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 h-10 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] text-sm">Отмена</button>
              <button onClick={() => deleteMaster(showDeleteConfirm)} className="flex-1 h-10 rounded-[12px] bg-red-500 text-white text-sm font-medium hover:bg-red-600" data-testid="confirm-delete-master">Удалить</button>
            </div>
          </Card>
        </div>
      )}

      {/* Commission Rule Add/Edit Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="rule-modal">
          <Card className="max-w-md w-full bg-card rounded-[24px] p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-['Playfair_Display'] text-xl">{editingRule ? 'Редактировать правило' : 'Новое правило'}</h3>
              <button onClick={() => setShowRuleModal(false)} className="p-1 rounded-lg hover:bg-[color:hsl(var(--muted))]"><IconX size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Мастер</label>
                <select value={ruleForm.master_id} onChange={e => setRuleForm(f => ({ ...f, master_id: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" data-testid="rule-form-master">
                  <option value="">Выберите мастера</option>
                  {masters.map(m => <option key={m.id} value={m.id}>{m.name} ({m.login})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Салон</label>
                <select value={ruleForm.salon_id} onChange={e => setRuleForm(f => ({ ...f, salon_id: e.target.value }))} className="w-full h-10 px-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" data-testid="rule-form-salon">
                  <option value="">Выберите салон</option>
                  {salons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Процент мастера: <span className="text-[color:hsl(var(--brand-orange))] font-bold">{ruleForm.master_percent}%</span> | Салону: <span className="text-blue-600 dark:text-blue-400 font-bold">{100 - ruleForm.master_percent}%</span></label>
                <Slider value={[ruleForm.master_percent]} onValueChange={v => setRuleForm(f => ({ ...f, master_percent: v[0] }))} max={100} step={5} className="w-full mt-2" data-testid="rule-form-percent" />
                <div className="flex justify-between text-xs text-[color:hsl(var(--foreground))]/60 mt-1"><span>0%</span><span>50%</span><span>100%</span></div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowRuleModal(false)} className="flex-1 h-11 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] text-sm font-medium">Отмена</button>
              <button onClick={saveRule} className="flex-1 h-11 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white text-sm font-medium hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors" data-testid="rule-form-save">{editingRule ? 'Сохранить' : 'Добавить'}</button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
